
import React from 'react';

export const UnderstandingValuation: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Understanding Apartment Building Valuation
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Property valuation is the foundation of successful apartment building sales. Understanding how professionals 
            determine market value helps you set realistic expectations and maximize your sale price.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">The Three Approaches to Valuation</h3>
            <div className="space-y-6">
              <div className="bg-teal-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold text-teal-800 mb-3">Income Approach</h4>
                <p className="text-gray-700 mb-3">
                  The most common method for apartment buildings, focusing on the property's income-generating capacity. 
                  This approach uses Net Operating Income (NOI) and capitalization rates to determine value.
                </p>
                <div className="bg-white p-4 rounded-lg border border-teal-200">
                  <strong className="text-teal-800">Formula:</strong> Property Value = NOI ÷ Cap Rate
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold text-blue-800 mb-3">Sales Comparison Approach</h4>
                <p className="text-gray-700 mb-3">
                  Compares your property to recently sold similar apartment buildings in the area. Adjustments are made 
                  for differences in size, condition, location, and amenities.
                </p>
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <strong className="text-blue-800">Key Factor:</strong> Recent comparable sales data
                </div>
              </div>

              <div className="bg-orange-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold text-orange-800 mb-3">Cost Approach</h4>
                <p className="text-gray-700 mb-3">
                  Calculates the cost to rebuild the property from scratch, minus depreciation, plus land value. 
                  Less commonly used for income properties but important for insurance purposes.
                </p>
                <div className="bg-white p-4 rounded-lg border border-orange-200">
                  <strong className="text-orange-800">Formula:</strong> Replacement Cost - Depreciation + Land Value
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/6df1213831c51779a65244e0e730aa2b.jpg"
              alt="Professional apartment building valuation and appraisal process"
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Factors That Impact Valuation</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Property Condition</h4>
              <p className="text-gray-600 text-sm">
                Age, maintenance history, recent improvements, and overall physical condition significantly impact value.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Location Quality</h4>
              <p className="text-gray-600 text-sm">
                Neighborhood desirability, proximity to amenities, transportation access, and local market conditions.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Income Potential</h4>
              <p className="text-gray-600 text-sm">
                Current rental rates, occupancy levels, lease terms, and potential for rent increases.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Market Trends</h4>
              <p className="text-gray-600 text-sm">
                Local supply and demand, interest rates, economic conditions, and investor appetite.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-scales-3-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Legal Factors</h4>
              <p className="text-gray-600 text-sm">
                Zoning restrictions, rent control laws, environmental issues, and any legal encumbrances.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Management Quality</h4>
              <p className="text-gray-600 text-sm">
                Property management efficiency, tenant satisfaction, and operational systems in place.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-teal-600 text-white p-6 rounded-xl max-w-4xl mx-auto">
            <h3 className="text-xl font-bold mb-3">Pro Tip: Getting an Accurate Valuation</h3>
            <p className="text-teal-100">
              For the most accurate valuation, hire a certified commercial appraiser who specializes in multifamily properties. 
              They'll provide a detailed report that considers all three approaches and local market factors, giving you a 
              defensible value for negotiations and financial planning.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
